import React, { type FC, useEffect, useRef, useState } from 'react'

import './_uploadedFile.scss'
import { useTranslation } from 'react-i18next'
import type ReactPlayer from 'react-player'

import { IconLocked, IconPencil, IconUnlocked } from 'assets/svg/sprite'
import { Icons } from 'helpers/icons'

const UploadedFile: FC<{
  type: string
  fileBlob: any
  cover?: boolean
  removeFile?: any
  toggleEdit?: any
  customClass?: string
  disablePlay?: boolean
  hasReorderCircle?: boolean
  reorderNumber?: number
  editPreview?: () => void
  imagePreview?: string
  trimmedVideo?: { start: number; end: number }
  isPremium?: boolean
  locked?: boolean
  handleToggleMediaLocked: () => void
  handleTogglePreviewOn?: () => void
  previewOn?: boolean
  setPreviewOpen?: (value: boolean) => void
  setPreviewMedia?: (value: any) => void
  mediaDta?: any
  attachmentCount?: number
}> = ({
  type,
  fileBlob,
  cover,
  removeFile,
  toggleEdit,
  customClass,
  disablePlay,
  hasReorderCircle,
  reorderNumber,
  editPreview,
  imagePreview,
  trimmedVideo,
  isPremium,
  locked,
  handleToggleMediaLocked,
  handleTogglePreviewOn,
  previewOn,
  setPreviewOpen,
  setPreviewMedia,
  mediaDta,
  attachmentCount
}) => {
  const [playing, setPlaying] = useState<boolean>(false)
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false)
  const audioRef = useRef<any>(null)
  const playerRef = useRef<ReactPlayer>(null)
  const { t } = useTranslation()


  const exif = mediaDta?.exif_media ? mediaDta.exif_media : mediaDta
  const entityId = exif?.entity_id
  const entityType = exif?.entity_type

  useEffect(() => {
    if (playerRef.current && trimmedVideo) {
      playerRef.current.seekTo(trimmedVideo.start, 'seconds')
    }
  }, [trimmedVideo, playerRef, videoPlaying])

  const isExif = !!mediaDta

  let borderClass = ''
  if (isExif) borderClass = 'uploadedfile--exif'
  else if (locked) borderClass = 'uploadedfile--locked'

  const displayUploadedFile = () => {
    switch (type) {
      case 'image':
        return <img className='img' src={fileBlob} alt={t('uploadedFile')} />

    case 'video':
      return (
        <video
          key={`${fileBlob}-${imagePreview ?? 'no-poster'}`}
          className='video'
          src={fileBlob}
          poster={imagePreview}
          muted
          playsInline
          preload='metadata'
          controls={false}
          onPlay={() => setVideoPlaying(true)}
          onPause={() => setVideoPlaying(false)}
          ref={el => {
            if (!el) return

            if (videoPlaying) {
              el.play().catch(error => {
                console.error('[UploadedFile][VIDEO_PLAY_ERROR]', {
                  error,
                  fileBlob,
                  imagePreview
                })
              })
            } else {
              el.pause()
            }
          }}
          onTimeUpdate={e => {
            const video = e.currentTarget

            if (trimmedVideo && video.currentTime >= trimmedVideo.end) {
              video.pause()
              setVideoPlaying(false)
              video.currentTime = trimmedVideo.start
            }
          }}
        />
      )

      case 'audio':
        return (
          <>
            <audio
              ref={audioRef}
              className='uploadedfile__audioplayer'
              src={fileBlob}
              onEnded={() => setPlaying(false)}
            />
            {!playing ? (
              <img
                onClick={(e) => {
                  e.stopPropagation()
                  audioRef.current.play()
                  setPlaying(true)
                }}
                className='uploadedfile__play'
                src={Icons.play}
                alt={t('play')}
              />
            ) : (
              <img
                onClick={(e) => {
                  e.stopPropagation()
                  audioRef.current.pause()
                  setPlaying(false)
                }}
                className='uploadedfile__play'
                src={Icons.pause}
                alt={t('pause')}
              />
            )}
          </>
        )

      default:
        return null
    }
  }

  return (
    <>
      <div
        style={{ zIndex: '0' }}
        className={`uploadedfile__wrapper ${customClass ?? ''}`}
        onClick={() => {
    
          if (entityType === 'Post' && typeof entityId === 'number') {
            setPreviewMedia?.(entityId)
            setPreviewOpen?.(true)
            return
          }

          editPreview?.()
        }}
      >
      {cover && type === 'image' && (attachmentCount ?? 0) > 1 && (
        <div className='uploadedfile__cover'>
          <div className='uploadedfile__cover__text'>{t('cover')}</div>
        </div>
      )}

{/*         {type === 'video' && isPremium && (
          <div
            className='uploadedfile__cover'
            onClick={(e) => {
              e.stopPropagation()
              handleTogglePreviewOn?.()
            }}
          >
            <div className='uploadedfile__cover__text'>
              {previewOn ? 'Preview On' : 'Preview Off'}
            </div>
          </div>
        )}
 */}
        <div
          className={`uploadedfile ${borderClass} ${
            cover && (attachmentCount ?? 0) > 1
              ? 'uploadedfile--cover'
              : ''
          } ${
            type === 'video' ? 'uploadedfile--video' : ''
          } ${
            type === 'audio' ? 'uploadedfile--audio' : ''
          }`}
        >
          {displayUploadedFile()}
        </div>

        {isPremium && (
          <div className='uploadedfile__premium'>
            <div
              className='uploadedfile__premium__icon'
              onClick={(e) => {
                e.stopPropagation()
                handleToggleMediaLocked()
              }}
            >
              {locked ? <IconLocked /> : <IconUnlocked />}
            </div>
          </div>
        )}

        {removeFile && (
          <div
            className='uploadedfile__remove'
            role='button'
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              removeFile()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                removeFile()
              }
            }}
          >
            <img
              src={Icons.close}
              alt={t('removeFile')}
            />
          </div>
        )}

        {hasReorderCircle && (
          <div className='uploadedfile__reorder'>
            <span className='uploadedfile__reorder__number'>
              {reorderNumber !== -1 ? reorderNumber! + 1 : ''}
            </span>
          </div>
        )}
      </div>

      {fileBlob && (
        <div
          style={{ zIndex: '999' }}
          className='uploadedfile__preview'
          onClick={(e) => {
            e.stopPropagation()
            editPreview?.()
          }}
        >
          <span className='uploadedfile__reorder__icon'>
            <IconPencil />
          </span>
        </div>
      )}

      {(type === 'video' || type === 'audio') && !disablePlay && (
        <div
          onClick={(e) => {
            e.stopPropagation()

            if (type === 'video') setVideoPlaying(!videoPlaying)
            if (type === 'audio') {
              if (playing) {
                audioRef.current.pause()
                setPlaying(false)
              } else {
                audioRef.current.play()
                setPlaying(true)
              }
            }
          }}
        >
          <img
            className='uploadedfile__play'
            src={
              type === 'video'
                ? videoPlaying
                  ? Icons.stop
                  : Icons.play
                : playing
                ? Icons.pause
                : Icons.play
            }
            alt={videoPlaying ? t('stop') : t('play')}
          />
        </div>
      )}
    </>
  )
}

export default UploadedFile